import React, { useDeferredValue, useEffect, useMemo, useState } from 'react';
import { supabase } from '../supabase';
import { useAuth } from '../context/AuthContext';
import { getGestionCostosSellPrice } from '../utils/productSellPrice';
import { fetchAllPaginated } from '../utils/productCatalog';
import {
  ArrowLeft,
  CheckSquare,
  Loader2,
  Percent,
  RefreshCcw,
  Search,
  Square,
  Sparkles
} from 'lucide-react';
import toast from 'react-hot-toast';

const safeNum = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
};

const round2 = (value) => Number(safeNum(value).toFixed(2));
const roundIncreasePrice = (value, enabled) => {
  const normalized = round2(value);
  if (!enabled) return normalized;
  return Math.round(normalized / 100) * 100;
};
const calculateIncreasedPrice = (currentPrice, factor, roundEnabled) =>
  roundIncreasePrice(safeNum(currentPrice) * safeNum(factor), roundEnabled);
const calculateIncreasedCost = (currentCost, factor) =>
  round2(safeNum(currentCost) * safeNum(factor));

const money = (value) =>
  safeNum(value).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const normalizeText = (value, fallback = '') => String(value || fallback).trim();
const normalizeFilterKey = (value, fallback = '') =>
  normalizeText(value, fallback)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, ' ')
    .toLowerCase();

export default function GestionCostosActualizaciones({ onNavigate }) {
  const { profile, organization } = useAuth();
  const orgId = profile?.organization_id;

  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);
  const [rows, setRows] = useState([]);
  const [variantMap, setVariantMap] = useState({});
  const [usdRate, setUsdRate] = useState(0);

  const [searchTerm, setSearchTerm] = useState('');
  const deferredSearchTerm = useDeferredValue(searchTerm);
  const [brandFilter, setBrandFilter] = useState('');
  const [productTypeFilter, setProductTypeFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const [increasePercent, setIncreasePercent] = useState('');
  const [roundIncrease, setRoundIncrease] = useState(false);

  useEffect(() => {
    if (!orgId) return;
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [productsRes, variantsRes, orgRes] = await Promise.all([
        fetchAllPaginated(() =>
          supabase
            .from('products')
            .select(`
              id,
              name,
              brand,
              product_type,
              category_id,
              cost_price,
              sell_price,
              price_usd,
              priced_in_usd,
              target_margin,
              product_categories:product_categories ( name )
            `)
            .eq('organization_id', orgId)
            .order('name', { ascending: true })
        ),
        fetchAllPaginated(() =>
          supabase
            .from('product_variants')
            .select('id, product_id, price, cost_price')
            .eq('organization_id', orgId)
            .order('product_id', { ascending: true })
        ),
        // usd_exchange_rate viene del AuthContext (un fetch por sesión).
        Promise.resolve({ data: organization, error: null })
      ]);

      if (orgRes.error) throw orgRes.error;

      const nextUsdRate = safeNum(orgRes.data?.usd_exchange_rate);
      const groupedVariants = {};

      (variantsRes || []).forEach((variant) => {
        if (!variant.product_id) return;
        if (!groupedVariants[variant.product_id]) groupedVariants[variant.product_id] = [];
        groupedVariants[variant.product_id].push({
          id: variant.id,
          price: safeNum(variant.price),
          cost_price: safeNum(variant.cost_price)
        });
      });

      const normalizedRows = (productsRes || []).map((product) => {
        const brandLabel = normalizeText(product.brand, 'Sin marca') || 'Sin marca';
        return {
          id: product.id,
          name: normalizeText(product.name),
          brand: brandLabel,
          brandKey: normalizeFilterKey(brandLabel, 'Sin marca'),
          productType: normalizeText(product.product_type, 'Producto Terminado') || 'Producto Terminado',
          categoryName: normalizeText(product.product_categories?.name, 'Sin categoría') || 'Sin categoría',
          cost_price: safeNum(product.cost_price),
          sell_price: getGestionCostosSellPrice(product, nextUsdRate),
          target_margin: safeNum(product.target_margin),
          price_usd: safeNum(product.price_usd),
          priced_in_usd: product.priced_in_usd === true,
          variantCount: (groupedVariants[product.id] || []).length
        };
      });

      setRows(normalizedRows);
      setVariantMap(groupedVariants);
      setUsdRate(nextUsdRate);
      setSelectedIds((prev) => prev.filter((id) => normalizedRows.some((row) => row.id === id)));
    } catch (error) {
      console.error('[GestionCostosActualizaciones] fetchData', error);
      toast.error('No pudimos cargar los productos.');
      setRows([]);
      setVariantMap({});
    } finally {
      setLoading(false);
    }
  };

  const brandOptions = useMemo(() => {
    const groupedBrands = new Map();

    rows.forEach((row) => {
      if (!groupedBrands.has(row.brandKey)) {
        groupedBrands.set(row.brandKey, { value: row.brandKey, label: row.brand });
        return;
      }

      const current = groupedBrands.get(row.brandKey);
      if (row.brand.localeCompare(current.label, 'es', { sensitivity: 'base' }) < 0) {
        groupedBrands.set(row.brandKey, { value: row.brandKey, label: row.brand });
      }
    });

    return Array.from(groupedBrands.values()).sort((a, b) =>
      a.label.localeCompare(b.label, 'es', { sensitivity: 'base' })
    );
  }, [rows]);

  const productTypeOptions = useMemo(() => {
    return Array.from(new Set(rows.map((row) => row.productType))).sort((a, b) => a.localeCompare(b));
  }, [rows]);

  const categoryOptions = useMemo(() => {
    return Array.from(new Set(rows.map((row) => row.categoryName))).sort((a, b) => a.localeCompare(b));
  }, [rows]);

  const filteredRows = useMemo(() => {
    const search = deferredSearchTerm.trim().toLowerCase();

    return rows.filter((row) => {
      const matchesSearch =
        !search ||
        row.name.toLowerCase().includes(search) ||
        row.brand.toLowerCase().includes(search) ||
        row.productType.toLowerCase().includes(search) ||
        row.categoryName.toLowerCase().includes(search);

      const matchesBrand = !brandFilter || row.brandKey === brandFilter;
      const matchesType = !productTypeFilter || row.productType === productTypeFilter;
      const matchesCategory = !categoryFilter || row.categoryName === categoryFilter;

      return matchesSearch && matchesBrand && matchesType && matchesCategory;
    });
  }, [rows, deferredSearchTerm, brandFilter, productTypeFilter, categoryFilter]);

  const visibleIds = useMemo(() => filteredRows.map((row) => row.id), [filteredRows]);
  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);

  const allVisibleSelected =
    visibleIds.length > 0 && visibleIds.every((id) => selectedSet.has(id));

  const increaseValue = safeNum(increasePercent);
  const hasValidIncrease = increaseValue > 0;

  const selectedRows = useMemo(() => rows.filter((row) => selectedSet.has(row.id)), [rows, selectedSet]);

  const selectionSummary = useMemo(() => {
    return selectedRows.reduce(
      (acc, row) => {
        acc.currentTotal += safeNum(row.sell_price);
        acc.projectedTotal += hasValidIncrease
          ? roundIncreasePrice(row.sell_price * (1 + increaseValue / 100), roundIncrease)
          : safeNum(row.sell_price);
        return acc;
      },
      { currentTotal: 0, projectedTotal: 0 }
    );
  }, [selectedRows, hasValidIncrease, increaseValue, roundIncrease]);

  const toggleOne = (id) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]));
  };

  const toggleAllVisible = () => {
    setSelectedIds((prev) => {
      if (allVisibleSelected) return prev.filter((id) => !visibleIds.includes(id));
      return Array.from(new Set([...prev, ...visibleIds]));
    });
  };

  const clearFilters = () => {
    setSearchTerm('');
    setBrandFilter('');
    setProductTypeFilter('');
    setCategoryFilter('');
  };

  const applyIncrease = async () => {
    if (!hasValidIncrease) {
      toast.error('Ingresá un porcentaje mayor a 0.');
      return;
    }

    if (selectedIds.length === 0) {
      toast.error('Seleccioná al menos un producto.');
      return;
    }

    setApplying(true);
    try {
      const factor = 1 + increaseValue / 100;
      const nextPricesById = {};
      const nextCostsById = {};
      const nextUsdById = {};
      const nextMarginsById = {};
      const nextVariants = {};
      const selectedRowsMap = rows.filter((row) => selectedSet.has(row.id));

      for (let index = 0; index < selectedRowsMap.length; index += 20) {
        const batch = selectedRowsMap.slice(index, index + 20);

        await Promise.all(
          batch.map(async (row) => {
            const newSellPrice = calculateIncreasedPrice(row.sell_price, factor, roundIncrease);
            const newCostPrice = calculateIncreasedCost(row.cost_price, factor);
            const payload = {
              sell_price: newSellPrice,
              cost_price: newCostPrice
            };

            // Solo recalculamos price_usd cuando el producto está explícitamente en USD.
            if (row.priced_in_usd === true && usdRate > 0) {
              payload.price_usd = round2(newSellPrice / usdRate);
              nextUsdById[row.id] = payload.price_usd;
            }

            if (newCostPrice > 0) {
              payload.target_margin = round2(((newSellPrice - newCostPrice) / newCostPrice) * 100);
              nextMarginsById[row.id] = payload.target_margin;
            }

            const { error: productError } = await supabase
              .from('products')
              .update(payload)
              .eq('id', row.id)
              .eq('organization_id', orgId);

            if (productError) throw productError;

            const productVariants = variantMap[row.id] || [];
            if (productVariants.length > 0) {
              const updatedVariants = productVariants.map((variant) => ({
                ...variant,
                price: calculateIncreasedPrice(variant.price, factor, roundIncrease),
                cost_price: newCostPrice
              }));

              await Promise.all(
                updatedVariants.map(async (variant) => {
                  const { error: variantError } = await supabase
                    .from('product_variants')
                    .update({
                      price: variant.price,
                      cost_price: variant.cost_price
                    })
                    .eq('id', variant.id)
                    .eq('organization_id', orgId);

                  if (variantError) throw variantError;
                })
              );

              nextVariants[row.id] = updatedVariants;
            }

            nextPricesById[row.id] = newSellPrice;
            nextCostsById[row.id] = newCostPrice;
          })
        );
      }

      setRows((prev) =>
        prev.map((row) => {
          if (nextPricesById[row.id] === undefined) return row;
          return {
            ...row,
            cost_price: nextCostsById[row.id] !== undefined ? nextCostsById[row.id] : row.cost_price,
            sell_price: nextPricesById[row.id],
            price_usd: nextUsdById[row.id] !== undefined ? nextUsdById[row.id] : row.price_usd,
            target_margin: nextMarginsById[row.id] !== undefined ? nextMarginsById[row.id] : row.target_margin
          };
        })
      );

      if (Object.keys(nextVariants).length > 0) {
        setVariantMap((prev) => ({ ...prev, ...nextVariants }));
      }

      toast.success(`Aumento aplicado a ${selectedRowsMap.length} producto(s).`);
    } catch (error) {
      console.error('[GestionCostosActualizaciones] applyIncrease', error);
      toast.error(error.message || 'No pudimos aplicar el aumento.');
    } finally {
      setApplying(false);
    }
  };

  return (
    <div className="w-full h-full p-3 sm:p-4 lg:p-6 animate-nova-in" translate="no">
      <div className="flex flex-col gap-4 mb-5">
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-3">
          <div className="flex items-start gap-3">
            <button
              type="button"
              onClick={() => onNavigate?.('gestion-costos')}
              className="w-11 h-11 rounded-2xl bg-white border border-slate-200 shadow-sm flex items-center justify-center text-slate-600 hover:text-[#51A7FD] transition-all"
              title="Volver a gestión de costos"
            >
              <ArrowLeft size={18} />
            </button>

            <div className="w-11 h-11 rounded-2xl bg-[#0B2A36] shadow-md flex items-center justify-center">
              <Sparkles size={18} className="text-[#3FF2AA]" />
            </div>

            <div>
              <h1 className="text-2xl font-black text-slate-800 uppercase tracking-tight">Actualizaciones Masivas</h1>
              <p className="text-[11px] font-bold uppercase tracking-[0.25em] text-slate-400 mt-1">
                Filtrá, tildá y aplicá aumentos de costo y venta en bloque
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={fetchData}
              disabled={loading || applying}
              className="px-4 py-2.5 rounded-2xl bg-white border border-slate-200 text-slate-600 shadow-sm hover:bg-slate-50 transition-all text-[10px] font-black uppercase tracking-widest flex items-center gap-2 disabled:opacity-60"
            >
              {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCcw size={14} />}
              Recargar
            </button>

            <button
              type="button"
              onClick={clearFilters}
              className="px-4 py-2.5 rounded-2xl bg-white border border-slate-200 text-slate-500 shadow-sm hover:bg-slate-50 transition-all text-[10px] font-black uppercase tracking-widest"
            >
              Limpiar filtros
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-3">
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4">
            <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Productos visibles</p>
            <p className="mt-2 text-3xl font-black text-slate-800 tracking-tight">{filteredRows.length}</p>
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4">
            <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Seleccionados</p>
            <p className="mt-2 text-3xl font-black text-slate-800 tracking-tight">{selectedIds.length}</p>
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4">
            <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Venta actual</p>
            <p className="mt-2 text-2xl font-black text-slate-800 tracking-tight">$ {money(selectionSummary.currentTotal)}</p>
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4">
            <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Venta proyectada</p>
            <p className="mt-2 text-2xl font-black text-slate-800 tracking-tight">$ {money(selectionSummary.projectedTotal)}</p>
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4">
            <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Aumento</p>
            <p className="mt-2 text-3xl font-black text-emerald-600 tracking-tight">
              {hasValidIncrease ? `+${increaseValue.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%` : '0%'}
            </p>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-[2rem] border border-slate-200 shadow-sm p-4 md:p-5 mb-4">
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-[minmax(0,1.2fr)_repeat(3,minmax(0,0.8fr))_minmax(0,1fr)] gap-3">
          <div>
            <label className="block text-[10px] font-black uppercase tracking-widest text-slate-400 mb-2">Buscar</label>
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-300" />
              <input
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder="Nombre, marca, tipo o categoría"
                className="w-full rounded-2xl border border-slate-200 bg-slate-50 pl-10 pr-4 py-3 text-[13px] font-semibold text-slate-700 outline-none focus:border-[#51A7FD] focus:bg-white transition-all"
              />
            </div>
          </div>

          <div>
            <label className="block text-[10px] font-black uppercase tracking-widest text-slate-400 mb-2">Marca</label>
            <select
              value={brandFilter}
              onChange={(event) => setBrandFilter(event.target.value)}
              className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-[13px] font-semibold text-slate-700 outline-none focus:border-[#51A7FD] focus:bg-white transition-all"
            >
              <option value="">Todas</option>
              {brandOptions.map((brand) => (
                <option key={brand.value} value={brand.value}>
                  {brand.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-[10px] font-black uppercase tracking-widest text-slate-400 mb-2">Tipo producto</label>
            <select
              value={productTypeFilter}
              onChange={(event) => setProductTypeFilter(event.target.value)}
              className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-[13px] font-semibold text-slate-700 outline-none focus:border-[#51A7FD] focus:bg-white transition-all"
            >
              <option value="">Todos</option>
              {productTypeOptions.map((productType) => (
                <option key={productType} value={productType}>
                  {productType}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-[10px] font-black uppercase tracking-widest text-slate-400 mb-2">Categoría</label>
            <select
              value={categoryFilter}
              onChange={(event) => setCategoryFilter(event.target.value)}
              className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-[13px] font-semibold text-slate-700 outline-none focus:border-[#51A7FD] focus:bg-white transition-all"
            >
              <option value="">Todas</option>
              {categoryOptions.map((category) => (
                <option key={category} value={category}>
                  {category}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-[10px] font-black uppercase tracking-widest text-slate-400 mb-2">Aumento %</label>
            <div className="flex flex-col sm:flex-row gap-2">
              <div className="relative flex-1">
                <Percent size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-emerald-400" />
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={increasePercent}
                  onChange={(event) => setIncreasePercent(event.target.value)}
                  placeholder="Ej. 12.5"
                  className="w-full rounded-2xl border border-emerald-200 bg-emerald-50 pl-10 pr-4 py-3 text-[13px] font-black text-emerald-700 outline-none focus:border-emerald-500 focus:bg-white transition-all"
                />
              </div>

              <button
                type="button"
                onClick={applyIncrease}
                disabled={applying || !hasValidIncrease || selectedIds.length === 0}
                className="px-5 py-3 rounded-2xl bg-[#22c55e] text-white shadow-lg hover:bg-[#16a34a] transition-all text-[10px] font-black uppercase tracking-widest flex items-center justify-center gap-2 disabled:opacity-60 disabled:hover:bg-[#22c55e]"
              >
                {applying ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
                Aplicar aumento
              </button>
            </div>
          </div>
        </div>

        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 mt-4">
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={toggleAllVisible}
              className={`px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all flex items-center gap-2 ${allVisibleSelected
                  ? 'bg-[#0B2A36] text-white shadow-md'
                  : 'bg-[#0B2A36] text-white border border-[#0B2A36] hover:opacity-95 shadow-md'
                }`}
            >
              {allVisibleSelected ? <CheckSquare size={14} /> : <Square size={14} />}
              {allVisibleSelected ? 'Destildar visibles' : 'Tildar visibles'}
            </button>

            <button
              type="button"
              onClick={() => setSelectedIds([])}
              className="px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest bg-[#0B2A36] text-white border border-[#0B2A36] hover:opacity-95 shadow-md transition-all"
            >
              Limpiar selección
            </button>
            <button
              type="button"
              onClick={() => setRoundIncrease((prev) => !prev)}
              className={`px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all border ${roundIncrease
                  ? 'bg-[#0B2A36] text-white border-[#0B2A36] shadow-md'
                  : 'bg-[#0B2A36] text-white border-[#0B2A36] hover:opacity-95 shadow-md'
                }`}
            >
              Redondear precio de venta
            </button>
          </div>

          <div className="space-y-1">
            <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">
              El aumento se aplica sobre el costo actual y sobre el precio de venta actual de cada producto.
            </p>
            {roundIncrease && (
              <p className="text-[10px] font-black uppercase tracking-widest text-emerald-600">
                El redondeo solo afecta al precio de venta: 00-49 baja y 50-99 sube a la centena siguiente.
              </p>
            )}
          </div>
        </div>
      </div>

      <div className="bg-white rounded-[2rem] border border-slate-200 shadow-sm overflow-hidden">
        <div className="bg-[#0B2A36] px-5 py-3 border-b border-white/10 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sparkles size={16} className="text-[#3FF2AA]" />
            <span className="text-[10px] font-black text-white uppercase tracking-widest">
              Actualización masiva ({filteredRows.length})
            </span>
          </div>
          <div className="text-[9px] font-bold uppercase tracking-widest text-white/60">
            {selectedIds.length} seleccionados
          </div>
        </div>

        <div className="md:hidden p-3 space-y-3 bg-slate-50/60">
          {loading ? (
            <div className="px-4 py-14 rounded-2xl bg-white border border-slate-200">
              <div className="flex items-center justify-center gap-3 text-slate-400">
                <Loader2 size={18} className="animate-spin" />
                <span className="text-[11px] font-black uppercase tracking-widest">Cargando productos</span>
              </div>
            </div>
          ) : filteredRows.length === 0 ? (
            <div className="px-4 py-14 rounded-2xl bg-white border border-slate-200 text-center">
              <p className="text-[11px] font-black uppercase tracking-widest text-slate-300">
                No encontramos productos con esos filtros
              </p>
            </div>
          ) : (
            filteredRows.map((row) => {
              const selected = selectedSet.has(row.id);
              const projectedCost = hasValidIncrease
                ? calculateIncreasedCost(row.cost_price, 1 + increaseValue / 100)
                : row.cost_price;
              const projectedPrice = hasValidIncrease
                ? roundIncreasePrice(row.sell_price * (1 + increaseValue / 100), roundIncrease)
                : row.sell_price;

              return (
                <div
                  key={row.id}
                  className={`rounded-2xl border p-4 shadow-sm bg-white ${selected ? 'border-emerald-200 bg-emerald-50/30' : 'border-slate-200'}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-[13px] font-black text-slate-800 tracking-tight break-words">{row.name}</p>
                      <p className="mt-1 text-[9px] font-black uppercase tracking-widest text-slate-400">
                        {row.variantCount > 0 ? `${row.variantCount} variante(s)` : 'Sin variantes'}
                      </p>
                    </div>

                    <button type="button" onClick={() => toggleOne(row.id)} className="bg-transparent p-1 rounded-lg shrink-0">
                      {selected ? (
                        <CheckSquare size={18} className="text-emerald-600" />
                      ) : (
                        <Square size={18} className="text-slate-300" />
                      )}
                    </button>
                  </div>

                  <div className="mt-3 grid grid-cols-2 gap-2 text-[10px]">
                    <div className="rounded-xl bg-slate-50 border border-slate-100 p-3">
                      <p className="font-black uppercase tracking-widest text-slate-400">Marca</p>
                      <p className="mt-1 font-black text-slate-700 break-words">{row.brand}</p>
                    </div>
                    <div className="rounded-xl bg-slate-50 border border-slate-100 p-3">
                      <p className="font-black uppercase tracking-widest text-slate-400">Tipo</p>
                      <p className="mt-1 font-black text-slate-700 break-words">{row.productType}</p>
                    </div>
                    <div className="rounded-xl bg-slate-50 border border-slate-100 p-3 col-span-2">
                      <p className="font-black uppercase tracking-widest text-slate-400">Categoría</p>
                      <p className="mt-1 font-black text-slate-700 break-words">{row.categoryName}</p>
                    </div>
                  </div>

                  <div className="mt-3 grid grid-cols-1 gap-2">
                    <div className="rounded-xl border border-slate-100 p-3">
                      <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Costo</p>
                      <p className="mt-1 text-[14px] font-black text-slate-700">$ {money(row.cost_price)}</p>
                    </div>
                    <div className="rounded-xl border border-amber-100 bg-amber-50/60 p-3">
                      <p className="text-[9px] font-black uppercase tracking-widest text-amber-700">Nuevo costo</p>
                      <p className="mt-1 text-[14px] font-black text-amber-700">$ {money(projectedCost)}</p>
                    </div>
                    <div className="rounded-xl border border-slate-100 p-3">
                      <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Precio actual</p>
                      <p className="mt-1 text-[14px] font-black text-slate-800">$ {money(row.sell_price)}</p>
                    </div>
                    <div className="rounded-xl border border-emerald-100 bg-emerald-50/60 p-3">
                      <p className="text-[9px] font-black uppercase tracking-widest text-emerald-700">Nuevo precio</p>
                      <p className="mt-1 text-[14px] font-black text-emerald-700">$ {money(projectedPrice)}</p>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        <div className="hidden md:block overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-100 text-[10px] font-black text-slate-400 uppercase tracking-widest">
                <th className="px-4 py-4 w-12 text-center">
                  <button type="button" onClick={toggleAllVisible} className="bg-transparent p-1 rounded-lg">
                    {allVisibleSelected ? (
                      <CheckSquare size={16} className="text-[#51A7FD]" />
                    ) : (
                      <Square size={16} className="text-slate-300" />
                    )}
                  </button>
                </th>
                <th className="px-4 py-4">Producto</th>
                <th className="px-4 py-4">Marca</th>
                <th className="px-4 py-4">Tipo</th>
                <th className="px-4 py-4">Categoría</th>
                <th className="px-4 py-4 text-right">Costo</th>
                <th className="px-4 py-4 text-right">Nuevo costo</th>
                <th className="px-4 py-4 text-right">Precio venta</th>
                <th className="px-4 py-4 text-right">Nuevo precio</th>
              </tr>
            </thead>

            <tbody className="divide-y divide-slate-50">
              {loading ? (
                <tr>
                  <td colSpan="9" className="px-4 py-14">
                    <div className="flex items-center justify-center gap-3 text-slate-400">
                      <Loader2 size={18} className="animate-spin" />
                      <span className="text-[11px] font-black uppercase tracking-widest">Cargando productos</span>
                    </div>
                  </td>
                </tr>
              ) : filteredRows.length === 0 ? (
                <tr>
                  <td colSpan="9" className="px-4 py-14 text-center">
                    <p className="text-[11px] font-black uppercase tracking-widest text-slate-300">
                      No encontramos productos con esos filtros
                    </p>
                  </td>
                </tr>
              ) : (
                filteredRows.map((row) => {
                  const selected = selectedSet.has(row.id);
                  const projectedCost = hasValidIncrease
                    ? calculateIncreasedCost(row.cost_price, 1 + increaseValue / 100)
                    : row.cost_price;
                  const projectedPrice = hasValidIncrease
                    ? roundIncreasePrice(row.sell_price * (1 + increaseValue / 100), roundIncrease)
                    : row.sell_price;

                  return (
                    <tr key={row.id} className={`hover:bg-slate-50/70 transition-all ${selected ? 'bg-emerald-50/40' : ''}`}>
                      <td className="px-4 py-4 text-center">
                        <button type="button" onClick={() => toggleOne(row.id)} className="bg-transparent p-1 rounded-lg">
                          {selected ? (
                            <CheckSquare size={16} className="text-emerald-600" />
                          ) : (
                            <Square size={16} className="text-slate-300" />
                          )}
                        </button>
                      </td>

                      <td className="px-4 py-4">
                        <div className="flex flex-col">
                          <span className="text-[12px] font-black text-slate-800 tracking-tight">{row.name}</span>
                          <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
                            {row.variantCount > 0 ? `${row.variantCount} variante(s)` : 'Sin variantes'}
                          </span>
                        </div>
                      </td>

                      <td className="px-4 py-4">
                        <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">{row.brand}</span>
                      </td>

                      <td className="px-4 py-4">
                        <span className="text-[10px] font-black uppercase tracking-widest text-slate-600">{row.productType}</span>
                      </td>

                      <td className="px-4 py-4">
                        <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">{row.categoryName}</span>
                      </td>

                      <td className="px-4 py-4 text-right">
                        <span className="text-[11px] font-black text-slate-700">$ {money(row.cost_price)}</span>
                      </td>

                      <td className="px-4 py-4 text-right">
                        <span className={`text-[11px] font-black ${hasValidIncrease ? 'text-amber-600' : 'text-slate-400'}`}>
                          $ {money(projectedCost)}
                        </span>
                      </td>

                      <td className="px-4 py-4 text-right">
                        <span className="text-[11px] font-black text-slate-800">$ {money(row.sell_price)}</span>
                      </td>

                      <td className="px-4 py-4 text-right">
                        <span className={`text-[11px] font-black ${hasValidIncrease ? 'text-emerald-600' : 'text-slate-400'}`}>
                          $ {money(projectedPrice)}
                        </span>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
